import React, { useEffect } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Skeleton } from 'antd';
import CodeSplittingErrorBoundry from './CodeSplittingErrorBoundry';
import { BaseFeatureToggles } from '../store/navigation/BaseFeatureToggles';
import { LayoutWrapper } from './LayoutWrapper';
import RuleSetPage from '../pages/PartnerConfigPages/Rulesets/RuleSetPage';
import RulePage from '../pages/PartnerConfigPages/Rulesets/RulePage';
import { V6OptinProtectedRoute } from './ReactRouterV6Components/V6OptinProtectedRoute';
import TntManagementPage from '../pages/TntManagementPage';
import AssetLedgerPage from '../pages/AdminPages/AssetLedger/AssetLedgerPage';
import { V6ShipiumAdminRoute } from './ReactRouterV6Components/V6ShipiumAdminRoute';
import PackagingConfigurationV2 from '../pages/PackagingConfigurationV2';
import { useSession } from '../hooks/useSession';
// Direct import (not lazy) to avoid react-pdf breaking useQuery in code-split chunks
import FedexOfflineLabelsAgreement from '../pages/FedexOfflineLabelsAgreement';
import CarrierEulaAgreement from '../pages/CarrierEulaAgreement';
import SimulationDetailPage from '../pages/Simulation/SimulationDetailPage';
import { useSessionStorage } from 'usehooks-ts';

const CostingLimitPage = React.lazy(
  () => import(/* webpackChunkName: "Costing Limit Page"*/ '../pages/CostingLimitPage'),
);

const AccountSettings = React.lazy(() => import(/* webpackChunkName: "AccountSettings" */ '../pages/AccountSettings'));
const AccessDenied = React.lazy(() => import(/* webpackChunkName: "AccessDenied" */ '../pages/AccessDenied'));
const NotFound = React.lazy(() => import(/* webpackChunkName: "NotFound" */ '../pages/Notfound'));
const RateCalculationTestPage = React.lazy(
  () => import(/* webpackChunkName: "RateCalculationTestPage" */ '../pages/RateCalculationTestPage'),
);
const SecurityConfig = React.lazy(() => import(/* webpackChunkName: "SecurityConfig" */ '../pages/SecurityConfig'));

const Tracking = React.lazy(() => import(/* webpackChunkName: "Tracking" */ '../pages/Tracking'));
const UploadedFilesPage = React.lazy(
  () => import(/* webpackChunkName: "UploadedFilesPage" */ '../pages/UploadedFiles'),
);
const ServiceStatusPage = React.lazy(
  () => import(/* webpackChunkName: "ServiceStatusPage" */ '../pages/ServiceStatus'),
);
const ServiceLevelReport = React.lazy(
  () => import(/* webpackChunkName: "ServiceLevelReport" */ '../pages/ServiceLevelReport'),
);
const PartnerConfigPage = React.lazy(
  () => import(/* webpackChunkName: "PartnerConfigPage" */ '../pages/PartnerConfigurationPage'),
);
const WebhooksPage = React.lazy(
  () => import(/* webpackChunkName: "WebhooksPage" */ '../pages/PartnerConfigPages/Webhooks/WebhooksPage'),
);
const WebhookDetailPage = React.lazy(
  () => import(/* webpackChunkName: "WebhookDetailPage" */ '../pages/PartnerConfigPages/Webhooks/WebhookDetailPage'),
);
const CarrierSelectionWebhooksPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CarrierSelectionWebhooksPage" */ '../pages/PartnerConfigPages/CarrierSelectionWebhooks/CarrierSelectionWebhooksPage'
    ),
);
const CarrierSelectionWebhookDetailPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CarrierSelectionWebhookDetailPage" */ '../pages/PartnerConfigPages/CarrierSelectionWebhooks/CarrierSelectionWebhookDetailPage'
    ),
);
const TeamManagement = React.lazy(() => import(/* webpackChunkName: "TeamManagement" */ '../pages/TeamManagement'));
const TeamManagementSso = React.lazy(
  () => import(/* webpackChunkName: "TeamManagementSso" */ '../pages/TeamManagementSso'),
);
const ZPLPreviewPage = React.lazy(() => import(/* webpackChunkName: "ZPLPreviewPage" */ '../pages/ZPLPreviewPage'));
const DocumentBlueprintsSummaryPage = React.lazy(() =>
  import(/* webpackChunkName: "DocumentBlueprintsSummaryPage" */ '../pages/DocumentBlueprints').then((m) => ({
    default: m.DocumentBlueprintsSummaryPage,
  })),
);
const DocumentBlueprintsAddPage = React.lazy(() =>
  import(/* webpackChunkName: "DocumentBlueprintsAddPage" */ '../pages/DocumentBlueprints').then((m) => ({
    default: m.DocumentBlueprintsAddPage,
  })),
);
const DocumentBlueprintsEditPage = React.lazy(() =>
  import(/* webpackChunkName: "DocumentBlueprintsEditPage" */ '../pages/DocumentBlueprints').then((m) => ({
    default: m.DocumentBlueprintsEditPage,
  })),
);
const DocumentBlueprintsDetailsPage = React.lazy(() =>
  import(/* webpackChunkName: "DocumentBlueprintsDetailsPage" */ '../pages/DocumentBlueprints').then((m) => ({
    default: m.DocumentBlueprintsDetailsPage,
  })),
);
const PromiseExplorerPage = React.lazy(() =>
  import(/* webpackChunkName: "PromiseExplorerPage" */ '../pages/PromiseExplorer/PromiseExplorerPage').then((m) => ({
    default: m.PromiseExplorerPage,
  })),
);
const Home = React.lazy(() => import(/* webpackChunkName: "Home" */ '../pages/Home'));
const ShipmentDetailsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "SearchShipmentsByIDPage" */ '../pages/ShipmentSearch/ShipmentDetails/ShipmentDetailsPage'
    ),
);
const LtlDetailsPage = React.lazy(
  () => import(/* webpackChunkName: "LtlDetailsPage" */ '../pages/ShipmentSearch/ShipmentDetails/Ltl/LtlDetailsPage'),
);
const SecurityManagementPage = React.lazy(
  () => import(/* webpackChunkName: "Security Management Page" */ '../pages/SecurityManagementPage'),
);
const PrintLabelsPage = React.lazy(
  () => import(/* webpackChunkName: "Print Labels Page" */ '../pages/PrintLabelsPage'),
);
const ShipmentSearchPageTypesense = React.lazy(
  () =>
    import(/* webpackChunkName: "ShipmentSearchPageTypesense" */ '../pages/ShipmentSearch/ShipmentSearchPageTypesense'),
);
const ShipmentVolumePage = React.lazy(
  () => import(/* webpackChunkName: "ShipmentVolumePage" */ '../pages/ShipmentVolume'),
);
const SummaryManifestReportPage = React.lazy(
  () => import(/* webpackChunkName: "SummaryManifestReport" */ '../pages/SummaryManifestReportPage'),
);
const ManifestPage = React.lazy(() => import(/* webpackChunkName: "Manifest" */ '../pages/ManifestPage'));
const SubscriptionTimingTestPage = React.lazy(
  () => import(/* webpackChunkName: "SubscriptionTimingTestPage" */ '../pages/SubscriptionTimingTestPage'),
);
const ConfigurationTreePage = React.lazy(
  () => import(/* webpackChunkName: "ConfigurationTreePage" */ '../pages/ConfigurationTreePage'),
);
const PartnerSecurityConfiguration = React.lazy(
  () => import(/* webpackChunkName: "Partner Security Configuration" */ '../pages/PartnerSecurityConfiguration'),
);
const BulkVoidLabels = React.lazy(
  () => import(/* webpackChunkName: "Bulk void labels page" */ '../pages/BulkVoidLabels'),
);
const WorkflowRoutes = React.lazy(() => import(/* webpackChunkName: "WorkFlowLandingPage"*/ '../pages/WorkflowRoutes'));
const WorkFlowWizard = React.lazy(() => import(/* webpackChunkName: "WorkFlowWizard"*/ '../pages/WorkFlowWizard'));
const V1AssetHistory = React.lazy(
  () => import(/* webpackChunkName: "V1AssetHistory"*/ '../pages/V1AssetHistoryTimeline'),
);

const LimitV2LandingPage = React.lazy(
  () => import(/*webpackChunkName: "Limit v2 landing"*/ '../pages/V2Limits/LimitV2LandingPage'),
);
const LimitV2Detail = React.lazy(
  () => import(/*webpackChunkName: "Limit v2 detail"*/ '../pages/V2Limits/LimitDetailPage'),
);
const V2LimitCreate = React.lazy(
  () => import(/*webpackChunkName: "Limit v2 create"*/ '../pages/V2Limits/LimitV2CreatePage'),
);
const DynamicLimitLandingPage = React.lazy(
  () => import(/*webpackChunkName: "Dynamic Limit Landing"*/ '../pages/V2Limits/DynamicLimitLandingPage'),
);
const DynamicLimitDetailPage = React.lazy(
  () => import(/*webpackChunkName: "Dynamic Limit Detail"*/ '../pages/V2Limits/DynamicLimitDetailPage'),
);
const DynamicLimitCreatePage = React.lazy(
  () => import(/*webpackChunkName: "Dynamic Limit Create"*/ '../pages/V2Limits/DynamicLimitCreatePage'),
);
const PartnerConfigPageV2 = React.lazy(
  () => import(/*webpackChunkName: "Limit v2 "*/ '../pages/PartnerConfigurationPageV2'),
);
const FulfillmentContextDetailPage = React.lazy(
  () => import(/*webpackChunkName: "Fulfillment Context Detail "*/ '../pages/FulfillmentContextDetailPage'),
);
const FulfillmentContextLandingPage = React.lazy(
  () => import(/*webpackChunkName: "Fulfillment Context Landing "*/ '../pages/FulfillmentContextLandingPage'),
);
const FulfillmentContextCreationPage = React.lazy(
  () => import(/*webpackChunkName: "Fulfillment Context Creation "*/ '../pages/FulfillmentContextCreationPage'),
);
const FulfillmentContextEditPage = React.lazy(
  () => import(/*webpackChunkName: "Fulfillment Context Edit "*/ '../pages/FulfillmentContextEditPage'),
);
const FulfillmentContextAddPage = React.lazy(
  () => import(/*webpackChunkName: "Fulfillment Context Add "*/ '../pages/FulfillmentContextAddPage'),
);
const FulfillmentContextRateDetailPage = React.lazy(
  () => import(/*webpackChunkName: "Fulfillment Context Rate Detail"*/ '../pages/FulfillmentContextRateDetailPage'),
);
const FulfillmentContextDockLaneDetailPage = React.lazy(
  () =>
    import(
      /*webpackChunkName: "Fulfillment Context Dock Lane Detail"*/ '../pages/FulfillmentContextDockLaneDetailPage'
    ),
);
const ShipOptionLandingPage = React.lazy(
  () => import(/*webpackChunkName: "Ship Option Landing "*/ '../pages/ShipOptionLandingPage'),
);
const ShipOptionDetailPage = React.lazy(
  () => import(/*webpackChunkName: "Ship Option Detail Page "*/ '../pages/ShipOptionDetailPage'),
);
const ZipZoneMappingsPage = React.lazy(
  () => import(/*webpackChunkName: "ZipZoneMappingsPage"*/ '../pages/AdminPages/ZipZoneMappings/ZipZoneMappingsPage'),
);
const AssetSummaryPage = React.lazy(
  () => import(/*webpackChunkName: "AssetSummaryPage"*/ '../pages/AdminPages/ConfigurationAssets/AssetSummaryPage'),
);
const AssetDetailsPage = React.lazy(
  () => import(/*webpackChunkName: "AssetDetailsPage"*/ '../pages/AdminPages/ConfigurationAssets/AssetDetailsPage'),
);
const ConfigurationImportPage = React.lazy(
  () =>
    import(
      /*webpackChunkName: "ConfigurationImportPage"*/ '../pages/AdminPages/ConfigurationImport/ConfigurationImportPage'
    ),
);
const SimulationLandingPage = React.lazy(
  () => import(/*webpackChunkName: "Simulation Landing Page"*/ '../pages/Simulation/SimulationLandingPage'),
);
const SimulationCreatePage = React.lazy(
  () => import(/*webpackChunkName: "Simulation Create Page"*/ '../pages/Simulation/SimulationCreatePage'),
);
const SimulationFileUploadsPage = React.lazy(
  () => import(/*webpackChunkName: "Simulation File Uploads Page"*/ '../pages/Simulation/SimulationFileUploadsPage'),
);
const SimulationFileUploadDetailPage = React.lazy(
  () =>
    import(
      /*webpackChunkName: "Simulation File Upload Detail Page"*/ '../pages/Simulation/SimulationFileUploadDetailPage'
    ),
);
const UPSReadyLandingPage = React.lazy(
  () => import(/*webpackChunkName: "UPS Ready Landing Page"*/ '../pages/UPSReadyLandingPage'),
);
const FedexRegistrationPage = React.lazy(
  () => import(/*webpackChunkName: "Fedex Registration Page"*/ '../pages/FedexRegistrationPage'),
);
const FedexAccountRegistration = React.lazy(
  () => import(/*webpackChunkName: "Fedex Account Registration"*/ '../pages/FedexAccountRegistration'),
);
const AmazonRegistrationPage = React.lazy(
  () => import(/*webpackChunkName: "Amazon Registration Page"*/ '../pages/AmazonRegistrationPage'),
);
const TikTokRegistrationPage = React.lazy(
  () => import(/*webpackChunkName: "TikTok Registration Page"*/ '../pages/TikTokRegistrationPage'),
);
const DatalakeExportPage = React.lazy(
  () => import(/*webpackChunkName: "Datalake Export Page"*/ '../pages/DatalakeExportPage'),
);
const MicroIncludePage = React.lazy(
  () => import(/*webpackChunkName: "Micro Include Page"*/ '../pages/MicroIncludePage'),
);
const SellRateSheetCreationPage = React.lazy(
  () =>
    import(/*webpackChunkName: "Sell Rate Sheet Creation Page"*/ '../pages/SellRateSheet/SellRateSheetCreationPage'),
);
const SellRateSheetEditPage = React.lazy(
  () => import(/*webpackChunkName: "Sell Rate Sheet Edit Page"*/ '../pages/SellRateSheet/SellRateSheetEditPage'),
);
const SellRateSheetLandingPage = React.lazy(
  () => import(/*webpackChunkName: "Sell Rate Sheet Landing Page"*/ '../pages/SellRateSheet/SellRateSheetLandingPage'),
);
const SellRateServiceMethodDetail = React.lazy(
  () =>
    import(
      /*webpackChunkName: "Sell Rate Servive Method Template Details"*/ '../pages/SellRateSheet/SellRateServiceMethodDetail'
    ),
);
const FulfillmentEngineConfigurationPage = React.lazy(
  () =>
    import(
      /*webpackChunkName: "Fulfillment Engine Configuration Page"*/ '../pages/FulfillmentEngine/FulfillmentEngineConfigPage'
    ),
);
const FulfillmentEngineSearchPage = React.lazy(
  () =>
    import(
      /*webpackChunkName: "Fulfillment Engine Search Page"*/ '../pages/FulfillmentEngine/FulfillmentEngineSearchPage'
    ),
);
const FulfillmentEnginePlanDetailsPage = React.lazy(
  () =>
    import(/*webpackChunkName: "Fulfillment Engine Plan Details Page"*/ '../pages/FulfillmentEngine/PlanDetailsPage'),
);
const FulfillmentEngineOrderDetailsPage = React.lazy(
  () =>
    import(/*webpackChunkName: "Fulfillment Engine Order Details Page"*/ '../pages/FulfillmentEngine/OrderDetailsPage'),
);
const BulkAssetProcessingLanding = React.lazy(
  () => import(/* webpackChunkName: "Bulk Processing Landing"*/ '../pages/BulkProcessing/BulkProcessingLanding'),
);
const BulkAssetProcessingDetail = React.lazy(
  () => import(/* webpackChunkName: "Bulk Processing Detail"*/ '../pages/BulkProcessing/BulkProcessingDetail'),
);

const Loading = <Skeleton active avatar loading={true} />;

export const PrivateRoute = ({ ...props }) => {
  const { sessionPartnerUrl, returnToLogin } = useSession();
  const history = useHistory();
  const [, setReturnUrl] = useSessionStorage('returnUrl', '');

  useEffect(() => {
    if (returnToLogin) {
      if (history.location?.pathname && history.location?.pathname !== '/login') {
        setReturnUrl(history.location?.pathname);
      }

      history.push('/login');
    }
  }, [returnToLogin, history, setReturnUrl]);

  return (
    <CodeSplittingErrorBoundry {...props}>
      <React.Suspense fallback={Loading}>
        <Switch>
          <Route
            path={`/${sessionPartnerUrl}`}
            render={() => (
              <LayoutWrapper flexibleWidth>
                <Home />
              </LayoutWrapper>
            )}
            exact
          />
          <V6OptinProtectedRoute
            requiredOptIn={[]}
            path={`/${sessionPartnerUrl}/print-labels`}
            requiredPermissionType="editor"
          >
            <PrintLabelsPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            excludedOptIn={['partner-sso-authentication-opt-in']}
            requiredOptIn={[]}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/team-management/manage-users`}
          >
            <TeamManagement />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['partner-sso-authentication-opt-in']}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/team-management/sso-users`}
          >
            <TeamManagementSso />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={[]}
            requiredPermissionType="viewer"
            path={`/${sessionPartnerUrl}/team-management/my-account`}
          >
            <AccountSettings />
          </V6OptinProtectedRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/configure/partner-config/:tab?`}>
            <PartnerConfigPageV2 />
          </V6ShipiumAdminRoute>
          <V6OptinProtectedRoute
            requiredOptIn={[]}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/configure/partner`}
          >
            <PartnerConfigPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredOptIn={[]}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/fulfillment-contexts/:fulfillmentContextId/carrierRateDetails/:contractId/:carrierRateId`}
          >
            <FulfillmentContextRateDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredFeatureMap={{ [BaseFeatureToggles.SHIPMENT_LANES]: true }}
            requiredOptIn={[]}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/fulfillment-contexts/:fulfillmentContextId/shipmentLaneSortConfiguration/:dockLaneSortConfigurationId/shipmentLaneDetails/:dockLaneId`}
          >
            <FulfillmentContextDockLaneDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredOptIn={[]}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/fulfillment-contexts/create`}
          >
            <FulfillmentContextCreationPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredOptIn={[]}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/fulfillment-contexts/:contextId/add/:addAction/:tabId?/:subTabId?`}
          >
            <FulfillmentContextAddPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredOptIn={[]}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/fulfillment-contexts/:contextId/edit/:editAction/:tabId?/:subTabId?`}
          >
            <FulfillmentContextEditPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredOptIn={[]}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/fulfillment-contexts/:contextId/:tabId?/:subTabId?`}
          >
            <FulfillmentContextDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredOptIn={[]}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/fulfillment-contexts`}
          >
            <FulfillmentContextLandingPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={[]}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/configure/ship-options/:shipOptionId`}
          >
            <ShipOptionDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={[]}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/configure/ship-options`}
          >
            <ShipOptionLandingPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['sell-ratesheets-opt-in']}
            requiredPermissionType="editor"
            path={`/${sessionPartnerUrl}/simulation/sell-rate-sheets/templates/:serviceMethodId`}
          >
            <SellRateServiceMethodDetail />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['sell-ratesheets-opt-in']}
            requiredPermissionType="editor"
            path={`/${sessionPartnerUrl}/simulation/sell-rate-sheets/create`}
          >
            <SellRateSheetCreationPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['sell-ratesheets-opt-in']}
            requiredPermissionType="editor"
            path={`/${sessionPartnerUrl}/simulation/sell-rate-sheets/:sellRateSheetId`}
          >
            <SellRateSheetEditPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['sell-ratesheets-opt-in']}
            path={`/${sessionPartnerUrl}/simulation/sell-rate-sheets`}
            requiredPermissionType="editor"
          >
            <SellRateSheetLandingPage />
          </V6OptinProtectedRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/simulation/upload/:fileUploadId`}>
            <SimulationFileUploadDetailPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/simulation/upload`}>
            <SimulationFileUploadsPage />
          </V6ShipiumAdminRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['simulation-opt-in']}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/simulation/create/:simulationType?`}
          >
            <SimulationCreatePage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['simulation-viewer-opt-in']}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/simulation/:simulationId/:tabId?/:subTabId?`}
          >
            <SimulationDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['simulation-viewer-opt-in']}
            requiredPermissionType={'editor'}
            path={`/${sessionPartnerUrl}/simulation`}
          >
            <SimulationLandingPage />
          </V6OptinProtectedRoute>
          {/* <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/fulfillment-contexts/:contextId`}><FulfillmentContextDetailPage /></V6ShipiumAdminRoute>
        <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/fulfillment-contexts`}><FulfillmentContextLandingPage /></V6ShipiumAdminRoute> */}
          <V6OptinProtectedRoute
            requiredOptIn={['tracking-opt-in']}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/tracking/webhooks/add`}
            layoutType="ConfigLayout"
          >
            <WebhookDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredPermissionType="administrator"
            requiredFeatureMap={{ [BaseFeatureToggles.CS_WEBHOOKS]: true }}
            path={`/${sessionPartnerUrl}/carrier-selection/carrier-selection-webhooks/add`}
            layoutType="ConfigLayout"
          >
            <CarrierSelectionWebhookDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['tracking-opt-in']}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/tracking/webhooks/:webhookId`}
            layoutType="ConfigLayout"
          >
            <WebhookDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredPermissionType="administrator"
            requiredFeatureMap={{ [BaseFeatureToggles.CS_WEBHOOKS]: true }}
            path={`/${sessionPartnerUrl}/carrier-selection/carrier-selection-webhooks/:webhookId`}
            layoutType="ConfigLayout"
          >
            <CarrierSelectionWebhookDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['tracking-opt-in']}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/tracking/webhooks`}
            layoutType="ConfigLayout"
          >
            <WebhooksPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={[]}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/configure/access-keys`}
          >
            <SecurityConfig />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            path={`/${sessionPartnerUrl}/carrier-selection/carrier-selection-report`}
            requiredPermissionType="viewer"
            flexibleWidth
          >
            <ShipmentVolumePage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            path={`/${sessionPartnerUrl}/carrier-selection/summary-manifest-report`}
            requiredFeatureMap={[]}
            requiredPermissionType="viewer"
          >
            <SummaryManifestReportPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            path={`/${sessionPartnerUrl}/carrier-selection/manifest/:actionType?`}
            requiredFeatureMap={{ [BaseFeatureToggles.AUTO_MANIFEST]: true }}
            requiredPermissionType="viewer"
            flexibleWidth
          >
            <ManifestPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['reporting-opt-in']}
            path={`/${sessionPartnerUrl}/delivery-reporting/exceptions/:report`}
            requiredPermissionType="viewer"
          >
            <ServiceLevelReport />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['tracking-info-request-opt-in']}
            path={`/${sessionPartnerUrl}/shipium-admin/tracking`}
            requiredPermissionType="viewer"
          >
            <Tracking />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            path={`/${sessionPartnerUrl}/delivery-reporting/shipment-volume`}
            requiredPermissionType="viewer"
            flexibleWidth
          >
            <ShipmentVolumePage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['reporting-opt-in', 'carrier-selection-opt-in']}
            path={`/${sessionPartnerUrl}/delivery-reporting/shipment/:shipmentId?`}
            requiredPermissionType="viewer"
            flexibleWidth
          >
            <ShipmentDetailsPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['reporting-opt-in', 'carrier-selection-opt-in']}
            path={`/${sessionPartnerUrl}/delivery-reporting/ltl/:ltlCostId?`}
            requiredFeatureMap={{ [BaseFeatureToggles.LTL_SHIPMENTS]: true }}
            requiredPermissionType="viewer"
          >
            <LtlDetailsPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            path={`/${sessionPartnerUrl}/delivery-reporting/shipment-search/:searchString`}
            requiredOptIn={['reporting-opt-in', 'carrier-selection-opt-in']}
            requiredPermissionType={'viewer'}
            flexibleWidth
          >
            <ShipmentSearchPageTypesense />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            path={`/${sessionPartnerUrl}/delivery-reporting/shipment-search`}
            requiredOptIn={['reporting-opt-in', 'carrier-selection-opt-in']}
            requiredPermissionType={'viewer'}
            flexibleWidth
          >
            <ShipmentSearchPageTypesense />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredPermissionType="viewer"
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredOptIn={[]}
            path={`/${sessionPartnerUrl}/configure/configuration-v2/configuration-tree/:workflowGroupKey/:assetUrn/:workflowKey/:workflowStepKey?`}
          >
            <WorkFlowWizard mode={'tree'} />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredPermissionType="viewer"
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredOptIn={[]}
            path={`/${sessionPartnerUrl}/configure/configuration-v2/configuration-tree/:workflowGroupKey?/:assetUrn?`}
          >
            <ConfigurationTreePage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredPermissionType="viewer"
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredOptIn={[]}
            path={`/${sessionPartnerUrl}/configure/configuration-v2`}
          >
            <WorkflowRoutes />
          </V6OptinProtectedRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/configure/ledger`}>
            <AssetLedgerPage />
          </V6ShipiumAdminRoute>
          ,
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/configure/import`}>
            <ConfigurationImportPage />
          </V6ShipiumAdminRoute>
          ,
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/configure/:assetType/:id`}>
            <AssetDetailsPage />
          </V6ShipiumAdminRoute>
          ,
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/configure/:assetType`}>
            <AssetSummaryPage />
          </V6ShipiumAdminRoute>
          ,
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/configure`}>
            <AssetSummaryPage />
          </V6ShipiumAdminRoute>
          ,
          <V6OptinProtectedRoute
            requiredOptIn={[]}
            requiredPermissionType="viewer"
            path={`/${sessionPartnerUrl}/service-status`}
          >
            <ServiceStatusPage />
          </V6OptinProtectedRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/subscription-test`}>
            <SubscriptionTimingTestPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/uploads`}>
            <UploadedFilesPage />
          </V6ShipiumAdminRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-tracking-api-opt-in']}
            path={`/${sessionPartnerUrl}/tracking`}
            requiredPermissionType="viewer"
          >
            <Tracking />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredPermissionType="editor"
            path={`/${sessionPartnerUrl}/carrier-selection/carrier-limits-v2/detail/:limitId/:tabId?`}
          >
            <LimitV2Detail />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredPermissionType="editor"
            path={`/${sessionPartnerUrl}/carrier-selection/carrier-limits-v2/create`}
          >
            <V2LimitCreate />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.UI_ORTHO_V2]: true }}
            requiredPermissionType="editor"
            flexibleWidth
            path={`/${sessionPartnerUrl}/carrier-selection/carrier-limits-v2`}
          >
            <LimitV2LandingPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.DYNAMIC_LIMITS]: true }}
            requiredPermissionType="editor"
            path={`/${sessionPartnerUrl}/carrier-selection/dynamic-limits/create`}
          >
            <DynamicLimitCreatePage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.DYNAMIC_LIMITS]: true }}
            requiredPermissionType="editor"
            path={`/${sessionPartnerUrl}/carrier-selection/dynamic-limits/:costAdjustConfigId`}
          >
            <DynamicLimitDetailPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.DYNAMIC_LIMITS]: true }}
            requiredPermissionType="editor"
            path={`/${sessionPartnerUrl}/carrier-selection/dynamic-limits`}
          >
            <DynamicLimitLandingPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.COSTING_LIMITS]: true }}
            path={`/${sessionPartnerUrl}/carrier-selection/shipment-cost-limits`}
            requiredPermissionType="editor"
          >
            <CostingLimitPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.RULE_SETS]: true }}
            path={`/${sessionPartnerUrl}/carrier-selection/rulesets/:ruleSetId/rules/:ruleId?`}
            requiredPermissionType="administrator"
          >
            <RulePage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.RULE_SETS]: true }}
            path={`/${sessionPartnerUrl}/carrier-selection/rulesets`}
            requiredPermissionType="administrator"
          >
            <RuleSetPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredFeatureMap={{ [BaseFeatureToggles.CS_WEBHOOKS]: true }}
            path={`/${sessionPartnerUrl}/carrier-selection/carrier-selection-webhooks`}
            requiredPermissionType="administrator"
          >
            <CarrierSelectionWebhooksPage />
          </V6OptinProtectedRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/configure/configuration-audit`}>
            <V1AssetHistory />
          </V6ShipiumAdminRoute>
          <V6OptinProtectedRoute
            requiredOptIn={[]}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/carrier-selection/bulk-label-void`}
          >
            <BulkVoidLabels />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredOptIn={['carrier-selection-opt-in']}
            requiredPermissionType="editor"
            path={`/${sessionPartnerUrl}/carrier-selection/export`}
          >
            <DatalakeExportPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            excludedOptIn={['partner-sso-authentication-opt-in']}
            requiredOptIn={[]}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/team-management/partner-security-configuration`}
          >
            <PartnerSecurityConfiguration />
          </V6OptinProtectedRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/rate-calculation-test`}>
            <RateCalculationTestPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/zpl-preview`}>
            <ZPLPreviewPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/document-blueprints/add`}>
            <DocumentBlueprintsAddPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/document-blueprints/:id/edit`}>
            <DocumentBlueprintsEditPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/document-blueprints/:id`}>
            <DocumentBlueprintsDetailsPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/document-blueprints`}>
            <DocumentBlueprintsSummaryPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/promise-explorer`}>
            <PromiseExplorerPage />
          </V6ShipiumAdminRoute>
          {/* TODO(2026-07-09): Remove this route once the Admin Console cutover is complete. (PIP-973) */}
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/security-management`}>
            <SecurityManagementPage />
          </V6ShipiumAdminRoute>
          {/* new orthogonality assets routes*/}
          <V6ShipiumAdminRoute
            path={`/${sessionPartnerUrl}/configuration/configuration-tree/:workflowGroupKey/:assetUrn/:workflowKey/:workflowStepKey?`}
          >
            <WorkFlowWizard mode={'tree'} />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/zip-zone-map`}>
            {' '}
            <ZipZoneMappingsPage />{' '}
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute
            path={`/${sessionPartnerUrl}/configuration/configuration-tree/:workflowGroupKey/:assetUrn/:workflowKey/:workflowStepKey?`}
          >
            <WorkFlowWizard mode={'tree'} />
          </V6ShipiumAdminRoute>
          <V6OptinProtectedRoute
            path={`/${sessionPartnerUrl}/configure/packaging-configuration`}
            requiredOptIn={[]}
            requiredPermissionType="editor"
          >
            <PackagingConfigurationV2 />
          </V6OptinProtectedRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/configure/time-in-transit-management`}>
            <TntManagementPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute
            path={`/${sessionPartnerUrl}/shipium-admin/bulk-asset-processing/:bulkProcessingUploadId`}
          >
            <BulkAssetProcessingDetail />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/shipium-admin/bulk-asset-processing`}>
            <BulkAssetProcessingLanding />
          </V6ShipiumAdminRoute>
          <V6OptinProtectedRoute
            requiredOptIn={[]}
            requiredFeatureMap={{ [BaseFeatureToggles.UPS_VERIFY]: true }}
            requiredPermissionType="administrator"
            path={`/${sessionPartnerUrl}/configure/ups-account-registration`}
            flexibleWidth
          >
            <UPSReadyLandingPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredPermissionType="administrator"
            requiredFeatureMap={[]}
            requiredOptIn={[]}
            path={`/${sessionPartnerUrl}/configure/fedex-account-registration/:carrierAccountId/:lifecycleStage?`}
          >
            <FedexAccountRegistration />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredPermissionType="administrator"
            requiredFeatureMap={[]}
            requiredOptIn={[]}
            path={`/${sessionPartnerUrl}/configure/fedex-account-registration`}
          >
            <FedexRegistrationPage />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredPermissionType="administrator"
            requiredFeatureMap={[]}
            requiredOptIn={['offline-labels-opt-in']}
            path={`/${sessionPartnerUrl}/configure/fedex-offline-labels-agreement`}
          >
            <FedexOfflineLabelsAgreement />
          </V6OptinProtectedRoute>
          <V6OptinProtectedRoute
            requiredPermissionType="administrator"
            requiredFeatureMap={[]}
            requiredOptIn={[]}
            path={`/${sessionPartnerUrl}/configure/carrier-eula/:legalAgreementType`}
          >
            <CarrierEulaAgreement />
          </V6OptinProtectedRoute>
          {
            // TODO open up to non shipium users
          }
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/configure/amazon-account-registration`} flexibleWidth>
            <AmazonRegistrationPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/configure/tiktok-account-registration`} flexibleWidth>
            <TikTokRegistrationPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/fulfillment-engine/plan/:planId/order/:orderId`}>
            <FulfillmentEngineOrderDetailsPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/fulfillment-engine/plan/:planId`}>
            <FulfillmentEnginePlanDetailsPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/fulfillment-engine-configuration`}>
            <FulfillmentEngineConfigurationPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/fulfillment-engine`}>
            <FulfillmentEngineSearchPage />
          </V6ShipiumAdminRoute>
          <V6ShipiumAdminRoute path={`/${sessionPartnerUrl}/micro-site-test`}>
            <MicroIncludePage microHostName={'micro-frontend-prototype'} />
          </V6ShipiumAdminRoute>
          <Route
            path={`/${sessionPartnerUrl}/access-denied`}
            render={() => (
              <LayoutWrapper>
                <AccessDenied />
              </LayoutWrapper>
            )}
          />
          <V6OptinProtectedRoute requiredOptIn={[]} requiredPermissionType="viewer" path="">
            <NotFound />
          </V6OptinProtectedRoute>
        </Switch>
      </React.Suspense>
    </CodeSplittingErrorBoundry>
  );
};
